import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import SEO, { SEO_CONFIG } from '@/components/SEO';
import {
  Check, X, Crown, Sparkles, Star, Shield, Zap, Award,
  Calendar, Apple, Calculator, MessageSquare, Target,
  Loader2, CheckCircle, RefreshCw, Users, TrendingUp,
  Heart, Brain, Coffee, ArrowRight, Gift
} from 'lucide-react';

const API = '/api';

// Plan features for comparison
const planFeatures = {
  basic: {
    name: 'Basic',
    price: { monthly: 249, yearly: 2490 },
    color: 'blue',
    icon: Star,
    popular: false,
    features: [
      { text: 'Ders kütüphanesine erişim', included: true },
      { text: 'Günlük check-in', included: true },
      { text: 'Temel öneri sistemi', included: true },
      { text: 'Aktivite/uyku takibi', included: true },
      { text: 'Topluluk okuma', included: true },
      { text: 'AI Diyetisyen', included: false },
      { text: 'Kişisel AI önerileri', included: false },
      { text: 'Haftalık beslenme planı', included: false },
      { text: 'Yemek fotoğraf analizi', included: false },
      { text: 'Koç mesajlaşma', included: false },
      { text: 'Öncelikli destek', included: false }
    ]
  },
  plus: {
    name: 'Plus',
    price: { monthly: 499, yearly: 4990 },
    color: 'purple',
    icon: Zap,
    popular: true,
    badge: 'En Popüler',
    features: [
      { text: 'Ders kütüphanesine erişim', included: true },
      { text: 'Günlük check-in', included: true },
      { text: 'Temel öneri sistemi', included: true },
      { text: 'Aktivite/uyku takibi', included: true },
      { text: 'Topluluk okuma', included: true },
      { text: 'AI Diyetisyen tam erişim', included: true, highlight: true },
      { text: 'Kişisel AI önerileri', included: true, highlight: true },
      { text: 'Haftalık beslenme planı', included: true, highlight: true },
      { text: 'Yemek fotoğraf analizi', included: true, highlight: true },
      { text: 'Koç mesajlaşma', included: false },
      { text: 'Öncelikli destek', included: false }
    ]
  },
  pro: {
    name: 'Pro',
    price: { monthly: 999, yearly: 9990 },
    color: 'amber',
    icon: Crown,
    popular: false,
    badge: 'Tam Paket',
    features: [
      { text: 'Ders kütüphanesine erişim', included: true },
      { text: 'Günlük check-in', included: true },
      { text: 'Temel öneri sistemi', included: true },
      { text: 'Aktivite/uyku takibi', included: true },
      { text: 'Topluluk tam erişim', included: true },
      { text: 'AI Diyetisyen sınırsız', included: true, highlight: true },
      { text: 'Kişisel AI önerileri', included: true },
      { text: 'Haftalık beslenme planı', included: true },
      { text: 'Yemek fotoğraf analizi', included: true },
      { text: 'Koç mesajlaşma', included: true, highlight: true },
      { text: 'Öncelikli destek', included: true, highlight: true }
    ]
  }
};

export default function PricingPage() {
  const navigate = useNavigate();
  const [isYearly, setIsYearly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [campaignCode, setCampaignCode] = useState('');
  const [appliedCampaign, setAppliedCampaign] = useState(null);

  useEffect(() => {
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setCheckingStatus(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/user/subscription`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentSubscription(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const applyCampaign = () => {
    if (campaignCode.toUpperCase() === 'HOSGELDIN') {
      setAppliedCampaign({
        code: 'HOSGELDIN',
        discount: 350,
        finalPrice: 149,
        description: 'İlk ay 149₺!'
      });
      toast.success('Kampanya kodu uygulandı! İlk ay 149₺');
    } else {
      toast.error('Geçersiz kampanya kodu');
    }
  };

  const handleSubscribe = async (planKey) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Lütfen giriş yapın');
      navigate('/login');
      return;
    }

    setLoading(true);
    setSelectedPlan(planKey);

    try {
      const planId = `${planKey}_${isYearly ? 'yearly' : 'monthly'}`;
      const params = new URLSearchParams({ plan_id: planId });
      
      if (appliedCampaign && planKey === 'plus' && !isYearly) {
        params.append('campaign_code', appliedCampaign.code);
      }

      const response = await axios.post(
        `${API}/plans/subscribe?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.payment_url) {
        window.location.href = response.data.payment_url;
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Bir hata oluştu');
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const getPrice = (plan) => {
    const price = isYearly ? plan.price.yearly : plan.price.monthly;
    
    // Apply campaign for Plus monthly
    if (plan.name === 'Plus' && !isYearly && appliedCampaign) {
      return appliedCampaign.finalPrice;
    }
    
    return price;
  };

  const getMonthlyEquivalent = (plan) => {
    if (isYearly) {
      return Math.round(plan.price.yearly / 12);
    }
    return plan.price.monthly;
  };

  const isCurrentPlan = (planKey) => {
    return currentSubscription?.current_plan === planKey;
  };

  const canUpgrade = (planKey) => {
    if (!currentSubscription?.is_premium) return true;
    const planOrder = ['basic', 'plus', 'pro'];
    const currentIndex = planOrder.indexOf(currentSubscription.current_plan);
    const targetIndex = planOrder.indexOf(planKey);
    return targetIndex > currentIndex;
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-purple-50 via-white to-purple-50">
      <SEO {...SEO_CONFIG.pricing} />
      {/* Hero */}
      <section className="py-12 text-center">
        <div className="max-w-4xl mx-auto px-4">
          <Badge className="bg-purple-100 text-purple-700 mb-4">
            <Sparkles className="h-3 w-3 mr-1" />
            Yeni Fiyatlandırma
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Size Uygun Planı Seçin
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Pilatika ile sağlıklı yaşamın kapılarını açın
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className={`text-sm font-medium ${!isYearly ? 'text-gray-900' : 'text-gray-500'}`}>
              Aylık
            </span>
            <Switch
              checked={isYearly}
              onCheckedChange={setIsYearly}
            />
            <span className={`text-sm font-medium ${isYearly ? 'text-gray-900' : 'text-gray-500'}`}>
              Yıllık
              <Badge className="ml-2 bg-green-100 text-green-700">2 ay bedava</Badge>
            </span>
          </div>

          {/* Current Subscription Status */}
          {currentSubscription?.is_premium && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8 inline-block">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <div className="text-left">
                  <p className="font-semibold text-green-800">
                    {currentSubscription.current_plan === 'plus' ? 'Plus' : 
                     currentSubscription.current_plan === 'pro' ? 'Pro' : 'Basic'} Plan Aktif
                  </p>
                  <p className="text-sm text-green-600">
                    Bitiş: {new Date(currentSubscription.premium_until).toLocaleDateString('tr-TR')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6">
            {Object.entries(planFeatures).map(([key, plan]) => {
              const Icon = plan.icon;
              const colorClasses = {
                blue: {
                  bg: 'bg-blue-50',
                  border: 'border-blue-200',
                  text: 'text-blue-600',
                  button: 'bg-blue-600 hover:bg-blue-700'
                },
                purple: {
                  bg: 'bg-purple-50',
                  border: 'border-purple-300 ring-2 ring-purple-200',
                  text: 'text-purple-600',
                  button: 'bg-purple-600 hover:bg-purple-700'
                },
                amber: {
                  bg: 'bg-amber-50',
                  border: 'border-amber-200',
                  text: 'text-amber-600',
                  button: 'bg-amber-600 hover:bg-amber-700'
                }
              };
              const colors = colorClasses[plan.color];

              return (
                <Card
                  key={key}
                  className={`relative ${plan.popular ? 'scale-105 shadow-xl' : 'shadow-lg'} ${colors.border}`}
                >
                  {plan.badge && (
                    <Badge className={`absolute -top-3 left-1/2 -translate-x-1/2 ${
                      plan.popular ? 'bg-purple-600' : 'bg-amber-600'
                    } text-white px-4 py-1`}>
                      {plan.badge}
                    </Badge>
                  )}

                  <CardHeader className={`${colors.bg} rounded-t-lg pt-8`}>
                    <div className="flex items-center justify-center mb-4">
                      <div className={`p-3 rounded-full ${colors.bg} border ${colors.border}`}>
                        <Icon className={`h-8 w-8 ${colors.text}`} />
                      </div>
                    </div>
                    <CardTitle className="text-2xl text-center">{plan.name}</CardTitle>
                    <div className="text-center mt-4">
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-4xl font-bold">{getPrice(plan).toLocaleString('tr-TR')}₺</span>
                        <span className="text-gray-500">/{isYearly ? 'yıl' : 'ay'}</span>
                      </div>
                      {isYearly && (
                        <p className="text-sm text-gray-500 mt-1">
                          Aylık ~{getMonthlyEquivalent(plan)}₺
                        </p>
                      )}
                      {key === 'plus' && !isYearly && appliedCampaign && (
                        <Badge className="mt-2 bg-green-100 text-green-700">
                          <Gift className="h-3 w-3 mr-1" />
                          {appliedCampaign.description}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent className="p-6">
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((feature, idx) => (
                        <li
                          key={idx}
                          className={`flex items-center gap-2 text-sm ${
                            feature.included ? 'text-gray-700' : 'text-gray-400'
                          }`}
                        >
                          {feature.included ? (
                            <Check className={`h-4 w-4 ${feature.highlight ? colors.text : 'text-green-500'}`} />
                          ) : (
                            <X className="h-4 w-4 text-gray-300" />
                          )}
                          <span className={feature.highlight ? 'font-medium' : ''}>
                            {feature.text}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      onClick={() => handleSubscribe(key)}
                      disabled={loading || isCurrentPlan(key) || !canUpgrade(key)}
                      className={`w-full ${colors.button} text-white`}
                    >
                      {loading && selectedPlan === key ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : isCurrentPlan(key) ? (
                        <>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Mevcut Plan
                        </>
                      ) : !canUpgrade(key) ? (
                        'Düşük Plan'
                      ) : (
                        <>
                          Başla
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Campaign Code Input */}
          <div className="max-w-md mx-auto mt-12">
            <Card className="border-dashed border-2 border-purple-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Gift className="h-5 w-5 text-purple-600" />
                  <span className="font-medium">Kampanya Kodunuz Var mı?</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    value={campaignCode}
                    onChange={(e) => setCampaignCode(e.target.value)}
                    placeholder="Kampanya kodu"
                    className="flex-1"
                  />
                  <Button
                    onClick={applyCampaign}
                    variant="outline"
                    className="border-purple-300 text-purple-600 hover:bg-purple-50"
                  >
                    Uygula
                  </Button>
                </div>
                {appliedCampaign && (
                  <p className="text-sm text-green-600 mt-2">
                    ✓ {appliedCampaign.code} kodu uygulandı
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Features Comparison */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Neden Premium?</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-green-100 p-4 rounded-full inline-block mb-4">
                <Apple className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">AI Diyetisyen</h3>
              <p className="text-gray-600 text-sm">
                Kişiselleştirilmiş beslenme planları, kalori hesaplama ve yemek analizi
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-purple-100 p-4 rounded-full inline-block mb-4">
                <Brain className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Günlük Check-in</h3>
              <p className="text-gray-600 text-sm">
                Ruh hali, enerji ve stres takibi ile kişiselleştirilmiş öneriler
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-amber-100 p-4 rounded-full inline-block mb-4">
                <Users className="h-8 w-8 text-amber-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Koç Desteği</h3>
              <p className="text-gray-600 text-sm">
                Pro ile birebir koç mesajlaşma ve öncelikli destek
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Sıkça Sorulan Sorular</h2>
          
          <div className="space-y-4">
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2">İstediğim zaman iptal edebilir miyim?</h3>
                <p className="text-gray-600 text-sm">
                  Evet, üyeliğinizi istediğiniz zaman iptal edebilirsiniz. İptal sonrası dönem sonuna kadar erişiminiz devam eder.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2">Plan yükseltme/düşürme yapabilir miyim?</h3>
                <p className="text-gray-600 text-sm">
                  Planınızı istediğiniz zaman yükseltebilirsiniz. Düşürme işlemi mevcut dönem sonunda geçerli olur.
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-2">HOSGELDIN kampanya kodu nasıl çalışır?</h3>
                <p className="text-gray-600 text-sm">
                  İlk kez Plus plan alan kullanıcılar ilk ay 149₺ öder. Sonraki aylar normal fiyat (499₺) uygulanır.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
